import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PlayIcon from '@mui/icons-material/PlayArrow';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Popper from '@mui/material/Popper';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import EditTicketDialog from 'components/common/EditTicketDialog';
import PageTitle from 'components/UI/PageTitle';
import useCurrentUser from 'hooks/useCurrentUser';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useAddMessageToTicketMutation,
  useAnalyzeTicketMutation,
  useGetTicketCostQuery,
  useGetTicketQuery,
  useSendTicketToRichardMutation,
  useTransitionTicketMutation,
  useUpdateTicketInterestedUsersMutation,
  useUpdateTicketMessageMutation,
  useUploadFileToTicketMutation,
} from 'services/api/tickets.api';
import { useGetUsersQuery } from 'services/api/user.api';
import { parseTicketEventName, TICKET_MOTIVE_LABELS, TICKET_URGENCY_LABELS } from 'services/types/ticket';
import {
  GLASS_BACKGROUND,
  GLASS_BLUR,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  STATUS_GOOD,
  STATUS_WARN,
  SURFACE,
  SURFACE_CONTAINER_LOW,
  SURFACE_CONTAINER_LOWEST,
  SURFACE_HOVER,
  SURFACE_TINT,
  YELLOW,
} from 'styles/tokens';
import { subscribeChannel } from 'utils/pusherClient';

import EditFileToolComponent from './components/EditFileToolComponent';
import EditPromptToolComponent from './components/EditPromptToolComponent';
import ExecuteQueryToolComponent from './components/ExecuteQueryToolComponent';
import GetClientInfoToolComponent from './components/GetClientInfoToolComponent';
import GrepSearchToolComponent from './components/GrepSearchToolComponent';
import ListDirToolComponent from './components/ListDirToolComponent';
import MarkAnalysisCompleteToolComponent from './components/MarkAnalysisCompleteToolComponent';
import ReadFileToolComponent from './components/ReadFileToolComponent';
import SimpleToolComponent from './components/SimpleToolComponent';
import TicketMessageComponent from './components/TicketMessageComponent';
import ToolResponseComponent from './components/ToolResponseComponent';

const FONT = 'Jost, sans-serif';

const primaryActionSx = {
  bgcolor: YELLOW,
  color: NAVY,
  borderRadius: RADIUS_COIN,
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': { bgcolor: alpha(YELLOW, 0.85), boxShadow: 'none' },
};

// Tonal variants — muted surfaces with the semantic color reserved for text/border.
const successActionSx = {
  bgcolor: alpha(STATUS_GOOD, 0.12),
  color: STATUS_GOOD,
  borderRadius: RADIUS_COIN,
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': { bgcolor: alpha(STATUS_GOOD, 0.2), boxShadow: 'none' },
};

const warnActionSx = {
  bgcolor: alpha(STATUS_WARN, 0.12),
  color: STATUS_WARN,
  borderRadius: RADIUS_COIN,
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': { bgcolor: alpha(STATUS_WARN, 0.2), boxShadow: 'none' },
};

const subtleActionSx = {
  bgcolor: alpha(NAVY, 0.05),
  color: NAVY,
  borderRadius: RADIUS_COIN,
  textTransform: 'none' as const,
  fontFamily: FONT,
  fontWeight: 600,
  boxShadow: 'none',
  '&:hover': { bgcolor: SURFACE_HOVER, boxShadow: 'none' },
};

// Glassmorphism dropdown surface for the "Analizar" hover menu.
const glassMenuSx = {
  mt: 0.5,
  minWidth: 220,
  backdropFilter: GLASS_BLUR,
  WebkitBackdropFilter: GLASS_BLUR,
  backgroundColor: GLASS_BACKGROUND,
  borderRadius: RADIUS_CARD,
  // Edge definition via an inset tonal ring instead of a 1px solid border
  // (design system §2: no 1px solid borders — separation by surface/shadow).
  boxShadow: `${SHADOW}, inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
  overflow: 'hidden',
  py: 0.5,
};

const menuItemSx = {
  borderRadius: RADIUS_ITEM,
  mx: 0.75,
  my: 0.25,
  gap: 1,
  fontFamily: FONT,
  fontSize: '0.85rem',
  color: NAVY,
  '& .MuiListItemIcon-root': { minWidth: 'auto', color: NAVY },
  '&:hover': { bgcolor: SURFACE_HOVER },
};

const brandSwitchSx = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: YELLOW, opacity: 1 },
};

const switchLabelSx = {
  '& .MuiFormControlLabel-label': {
    fontFamily: FONT,
    fontSize: '0.7rem',
    color: `${NAVY}BB`,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    fontWeight: 600,
  },
};

const metaTextSx = {
  fontFamily: FONT,
  fontSize: '0.7rem',
  color: `${NAVY}99`,
  letterSpacing: '0.03em',
};

// Value style for the description card rows (Motivo, Urgencia, Business Unit, Descripción).
const fieldValueSx = {
  fontFamily: FONT,
  fontSize: '0.95rem',
  color: `${NAVY}DD`,
  lineHeight: 1.5,
};

const Ticket: React.FC = () => {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const messageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const user = useCurrentUser();
  const { data: ticket, isLoading, refetch } = useGetTicketQuery(Number(ticketId));
  const { data: costData } = useGetTicketCostQuery(Number(ticketId));
  const [addMessage, { isLoading: isSendingMessage }] = useAddMessageToTicketMutation();
  const [uploadFile] = useUploadFileToTicketMutation();
  const [analyzeTicket] = useAnalyzeTicketMutation();
  const [sendToRichard, { isLoading: isSendingToRichard }] = useSendTicketToRichardMutation();
  const [transitionTicket, { isLoading: isTransitioning }] = useTransitionTicketMutation();
  const [updateTicketMessage] = useUpdateTicketMessageMutation();
  const [updateInterestedUsers, { isLoading: isUpdatingInterestedUsers }] = useUpdateTicketInterestedUsersMutation();

  const [newMessage, setNewMessage] = useState('');
  const [showToUser, setShowToUser] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [analyzeMenuOpen, setAnalyzeMenuOpen] = useState(false);
  const [richardSessionUrl, setRichardSessionUrl] = useState<string | null>(null);
  const [richardError, setRichardError] = useState<string | null>(null);
  const analyzeWrapRef = useRef<HTMLDivElement>(null);
  const [viewAIAnalysis, setViewAIAnalysis] = useState(false);
  const useRegularUserView = !viewAIAnalysis || !user?.isAdmin;
  const [assignAdminOpen, setAssignAdminOpen] = useState(false);
  const [adminSearch, setAdminSearch] = useState('');
  const assignAdminAnchorRef = useRef<HTMLButtonElement>(null);
  const adminSearchRef = useRef<HTMLInputElement>(null);

  const { data: allUsers } = useGetUsersQuery({}, { skip: !user?.isAdmin });
  const adminUsers = (allUsers || [])
    .filter((u) => u.isAdmin)
    .slice()
    .sort((a, b) => {
      if (a.id === user?.id) return -1;
      if (b.id === user?.id) return 1;
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  const assignedAdminIds = (ticket?.interestedUserIds || []).filter((id) => adminUsers.some((a) => a.id === id));

  const filteredAdminUsers = useMemo(() => {
    if (!adminSearch) return adminUsers;
    const lower = adminSearch.toLowerCase();
    return adminUsers.filter(
      (u) => (u.name || '').toLowerCase().includes(lower) || (u.email || '').toLowerCase().includes(lower)
    );
  }, [adminUsers, adminSearch]);

  useEffect(() => {
    if (assignAdminOpen) {
      const id = setTimeout(() => adminSearchRef.current?.focus(), 50);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [assignAdminOpen]);

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    if (!ticketId) {
      return undefined;
    }

    const channelName = `ticket-${ticketId}`;
    const { channel, unsubscribe } = subscribeChannel(channelName);
    const handler = () => {
      refetchRef.current().catch((error) => {
        console.error('Error refetching ticket:', error);
      });
    };
    channel.bind('update', handler);

    return () => {
      channel.unbind('update', handler);
      unsubscribe();
    };
  }, [ticketId]);

  useEffect(() => {
    if (messageInputRef.current) {
      messageInputRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ticket?.AIHistory]);

  const handleAddMessage = async () => {
    if (!newMessage.trim() || !ticketId) return;

    try {
      await addMessage({
        ticketId: Number(ticketId),
        data: { content: newMessage, showToUser },
      }).unwrap();

      setNewMessage('');
      refetch().catch((error) => {
        console.error('Error refetching ticket:', error);
      });
    } catch (error) {
      console.error('Error adding message:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !ticketId) return;

    setIsUploadingFile(true);
    setUploadError(null);

    try {
      uploadFile({ ticketId: Number(ticketId), file, showToUser })
        .unwrap()
        .then(() => {
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          refetch().catch((error) => {
            console.error('Error refetching ticket:', error);
          });
        })
        .catch((err) => {
          setUploadError('Error al subir el archivo');
          console.error('Error uploading file:', err);
        });
    } catch (err) {
      setUploadError('Error al subir el archivo');
      console.error('Error uploading file:', err);
    } finally {
      setIsUploadingFile(false);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          const imageFile = new File([file], `pasted-image-${Date.now()}.png`, { type: file.type });

          uploadFile({ ticketId: Number(ticketId), file: imageFile, showToUser })
            .unwrap()
            .then(() => {
              refetch().catch((error) => {
                console.error('Error refetching ticket:', error);
              });
            })
            .catch((error) => {
              console.error('Error uploading pasted image:', error);
              setUploadError('Error al subir la imagen pegada');
            });
          event.preventDefault();
          break;
        }
      }
    }
  };

  const handlePasteWrapper = (event: React.ClipboardEvent) => {
    handlePaste(event);
  };

  const handleAnalyzeTicket = async () => {
    if (!ticketId) return;

    try {
      await analyzeTicket(Number(ticketId)).unwrap();
      refetch().catch((error) => {
        console.error('Error refetching ticket:', error);
      });
    } catch (error) {
      console.error('Error analyzing ticket:', error);
    }
  };

  const handleSendToRichard = async () => {
    if (!ticketId) return;
    setRichardError(null);
    setRichardSessionUrl(null);
    try {
      const result = await sendToRichard(Number(ticketId)).unwrap();
      setRichardSessionUrl(result.url);
      window.open(result.url, '_blank', 'noopener,noreferrer');
      refetch().catch((error) => {
        console.error('Error refetching ticket:', error);
      });
    } catch (error) {
      console.error('Error sending ticket to Richard:', error);
      setRichardError('No se pudo enviar el ticket a Richard');
    }
  };

  const toggleAdmin = async (adminId: number) => {
    if (!ticketId) return;
    const nextIds = assignedAdminIds.includes(adminId)
      ? assignedAdminIds.filter((id) => id !== adminId)
      : [...assignedAdminIds, adminId];
    try {
      await updateInterestedUsers({ ticketId: Number(ticketId), userIds: nextIds }).unwrap();
      refetch().catch((error) => {
        console.error('Error refetching ticket:', error);
      });
    } catch (error) {
      console.error('Error updating assigned admins:', error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleAddMessage().catch((error) => {
        console.error('Error handling key press:', error);
      });
    }
  };

  const handleTicketTransition = (event: 'CLOSE' | 'NEW_MESSAGE' | 'DISMISS') => {
    if (!ticketId) return;
    transitionTicket({ ticketId: Number(ticketId), data: { event } })
      .unwrap()
      .then(refetch)
      .catch((error) => {
        console.error('Error transitioning ticket:', error);
      });
  };

  const handleEditMessage = async (messageIndex: number, content: string) => {
    if (!ticketId) return;

    try {
      await updateTicketMessage({
        ticketId: Number(ticketId),
        messageIndex,
        data: { content },
      }).unwrap();

      await refetch();
    } catch (error) {
      console.error('Error updating message:', error);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: SURFACE,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: NAVY }} />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: SURFACE,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <Typography sx={{ fontFamily: FONT, fontSize: '1rem', color: `${NAVY}99` }}>Ticket no encontrado</Typography>
        <Button onClick={() => navigate('/tickets')} sx={subtleActionSx}>
          Volver a Tickets
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: SURFACE, pb: 6 }}>
      <Box sx={{ maxWidth: 1100, margin: '0 auto', px: { xs: 2, md: 3 }, pt: 2 }}>
        {/* Header: back button + title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2,
          }}
        >
          <IconButton
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate('/tickets');
              }
            }}
            size="medium"
            sx={{
              color: NAVY,
              borderRadius: RADIUS_ITEM,
              '&:hover': { bgcolor: SURFACE_HOVER },
            }}
          >
            <ArrowBackIcon fontSize="medium" />
          </IconButton>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PageTitle sx={{ mt: 0, mb: 0 }}>
              <Box component="span" sx={{ fontWeight: 400 }}>
                [#{ticket.id}]
              </Box>{' '}
              {ticket.title}
            </PageTitle>
          </Box>
        </Box>

        {/* Action buttons */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          {user?.isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={viewAIAnalysis}
                  onChange={() => setViewAIAnalysis((prev) => !prev)}
                  sx={brandSwitchSx}
                />
              }
              label="Ver análisis IA"
              labelPlacement="start"
              sx={switchLabelSx}
            />
          )}
          {user?.isAdmin && adminUsers.length > 0 && (
            <>
              <Button
                ref={assignAdminAnchorRef}
                onClick={() => setAssignAdminOpen(true)}
                disabled={isUpdatingInterestedUsers}
                endIcon={
                  isUpdatingInterestedUsers ? (
                    <CircularProgress size={14} sx={{ color: NAVY }} />
                  ) : (
                    <KeyboardArrowDownIcon sx={{ color: NAVY }} />
                  )
                }
                sx={{
                  minWidth: 180,
                  maxWidth: 280,
                  textTransform: 'none',
                  fontFamily: FONT,
                  fontSize: '0.8rem',
                  color: NAVY,
                  borderRadius: RADIUS_COIN,
                  bgcolor: alpha(NAVY, 0.05),
                  py: 0.75,
                  px: 1.5,
                  justifyContent: 'space-between',
                  '&:hover': { bgcolor: SURFACE_HOVER },
                  '& .MuiButton-endIcon': { ml: 0.5 },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    textAlign: 'left',
                  }}
                >
                  {assignedAdminIds.length === 0
                    ? 'Asignar admin'
                    : adminUsers
                        .filter((u) => assignedAdminIds.includes(u.id))
                        .map((u) => `${u.name || u.email}${u.id === user?.id ? ' (yo)' : ''}`)
                        .join(', ')}
                </Box>
              </Button>
              <Popover
                open={assignAdminOpen}
                anchorEl={assignAdminAnchorRef.current}
                onClose={() => {
                  setAssignAdminOpen(false);
                  setAdminSearch('');
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                  paper: {
                    sx: {
                      ...glassMenuSx,
                      minWidth: assignAdminAnchorRef.current?.offsetWidth ?? 240,
                      maxHeight: 360,
                      display: 'flex',
                      flexDirection: 'column',
                    },
                  },
                }}
              >
                <Box sx={{ p: 1, pb: 0.5 }}>
                  <TextField
                    size="small"
                    placeholder="Buscar…"
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                    inputRef={adminSearchRef}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: RADIUS_ITEM,
                        fontFamily: FONT,
                        fontSize: '0.85rem',
                      },
                    }}
                  />
                </Box>
                <Box sx={{ maxHeight: 280, overflowY: 'auto', py: 0.5 }}>
                  {filteredAdminUsers.length === 0 ? (
                    <Typography
                      sx={{
                        px: 2,
                        py: 1.5,
                        color: `${NAVY}80`,
                        fontSize: '0.85rem',
                        fontFamily: FONT,
                      }}
                    >
                      Sin resultados
                    </Typography>
                  ) : (
                    filteredAdminUsers.map((u) => {
                      const isSelf = u.id === user?.id;
                      const label = `${u.name || u.email}${isSelf ? ' (yo)' : ''}`;
                      const checked = assignedAdminIds.includes(u.id);
                      return (
                        <Box
                          key={u.id}
                          role="option"
                          aria-selected={checked}
                          onClick={() => {
                            toggleAdmin(u.id).catch(console.error);
                          }}
                          sx={{
                            ...menuItemSx,
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.5,
                            cursor: 'pointer',
                          }}
                        >
                          <Checkbox checked={checked} size="small" sx={{ color: NAVY, p: 0.5 }} />
                          <Typography
                            sx={{
                              fontFamily: FONT,
                              fontSize: '0.85rem',
                              color: NAVY,
                              fontWeight: isSelf ? 600 : 400,
                              ml: 0.5,
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {label}
                          </Typography>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Popover>
            </>
          )}
          <Button startIcon={<EditIcon />} onClick={() => setEditDialogOpen(true)} size="small" sx={primaryActionSx}>
            Editar
          </Button>
          {user?.isAdmin && ticket?.businessUnitId && (
            <Button
              startIcon={<SettingsIcon />}
              onClick={() => window.open(`/businessUnit-config/${ticket.businessUnitId}`, '_blank')}
              size="small"
              sx={subtleActionSx}
            >
              Configurar Business Unit
            </Button>
          )}
          {user?.isAdmin && (
            <Box
              ref={analyzeWrapRef}
              onMouseEnter={() => setAnalyzeMenuOpen(true)}
              onMouseLeave={() => setAnalyzeMenuOpen(false)}
              sx={{ position: 'relative', display: 'inline-flex' }}
            >
              <Button
                startIcon={<PlayIcon />}
                onClick={() => {
                  handleAnalyzeTicket().catch(console.error);
                }}
                disabled={ticket?.status === 'ANALYSIS_WITH_AI'}
                size="small"
                sx={subtleActionSx}
              >
                Analizar
              </Button>
              <Popper
                open={analyzeMenuOpen}
                anchorEl={analyzeWrapRef.current}
                placement="bottom-end"
                disablePortal
                style={{ zIndex: 1300 }}
              >
                <Paper sx={glassMenuSx}>
                  <MenuList dense disablePadding>
                    <MenuItem
                      onClick={() => {
                        setAnalyzeMenuOpen(false);
                        handleAnalyzeTicket().catch(console.error);
                      }}
                      disabled={ticket?.status === 'ANALYSIS_WITH_AI'}
                      sx={menuItemSx}
                    >
                      <ListItemIcon>
                        <PlayIcon fontSize="small" />
                      </ListItemIcon>
                      Análisis legacy
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setAnalyzeMenuOpen(false);
                        handleSendToRichard().catch(console.error);
                      }}
                      disabled={isSendingToRichard}
                      sx={menuItemSx}
                    >
                      <ListItemIcon>
                        {isSendingToRichard ? (
                          <CircularProgress size={16} sx={{ color: NAVY }} />
                        ) : (
                          <AutoAwesomeIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      Enviar a Richard
                    </MenuItem>
                  </MenuList>
                </Paper>
              </Popper>
            </Box>
          )}
          {user?.isAdmin &&
            (ticket.status === 'CLOSED' || ticket.status === 'DISMISSED' ? (
              <Button
                onClick={() => handleTicketTransition('NEW_MESSAGE')}
                disabled={isTransitioning}
                size="small"
                sx={warnActionSx}
              >
                Marcar como pendiente
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => handleTicketTransition('CLOSE')}
                  disabled={isTransitioning}
                  size="small"
                  sx={successActionSx}
                >
                  Marcar como completado
                </Button>
                {!ticket.businessUnitId && (
                  <Button
                    onClick={() => handleTicketTransition('DISMISS')}
                    disabled={isTransitioning}
                    size="small"
                    sx={subtleActionSx}
                  >
                    Descartar
                  </Button>
                )}
              </>
            ))}
        </Box>

        {/* Description card */}
        <Box
          sx={{
            bgcolor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_CARD,
            p: 2,
            mb: 2,
          }}
        >
          {ticket.motive && (
            <Typography sx={{ ...fieldValueSx, mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 700, color: `${NAVY}99` }}>
                Motivo:{' '}
              </Box>
              {TICKET_MOTIVE_LABELS[ticket.motive]}
            </Typography>
          )}
          {ticket.urgency && (
            <Typography sx={{ ...fieldValueSx, mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 700, color: `${NAVY}99` }}>
                Urgencia:{' '}
              </Box>
              {TICKET_URGENCY_LABELS[ticket.urgency]}
            </Typography>
          )}
          <Typography sx={{ ...fieldValueSx, mb: 1 }}>
            <Box component="span" sx={{ fontWeight: 700, color: `${NAVY}99` }}>
              Business Unit:{' '}
            </Box>
            {ticket.businessUnit?.name || 'General'}
          </Typography>
          <Typography sx={{ ...fieldValueSx, whiteSpace: 'pre-line' }}>
            <Box component="span" sx={{ fontWeight: 700, color: `${NAVY}99` }}>
              Descripción:{' '}
            </Box>
            {showFullDescription ? ticket.description : ticket.description.substring(0, 1000)}
            {!showFullDescription && ticket.description.length > 1000 && '...'}
          </Typography>
          {ticket.description.length > 1000 && (
            <Button
              size="small"
              onClick={() => setShowFullDescription(!showFullDescription)}
              sx={{
                mt: 1,
                p: 0,
                minWidth: 'auto',
                textTransform: 'none',
                fontFamily: FONT,
                fontWeight: 600,
                color: NAVY,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
              }}
            >
              {showFullDescription ? 'Ver menos' : 'Ver más'}
            </Button>
          )}
        </Box>

        {/* Meta row — only rendered when it has content, so it doesn't add empty space. */}
        {((user?.isAdmin && costData && costData.cost > 0) || ticket.clientId) && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            {user?.isAdmin && costData && costData.cost > 0 && (
              <Typography sx={metaTextSx}>Costo: ${costData?.cost.toFixed(2)}</Typography>
            )}
            {ticket.clientId && (
              <Typography
                component="a"
                href={`/clients/${ticket.clientId}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ...metaTextSx,
                  color: NAVY,
                  fontWeight: 600,
                  textDecoration: 'none',
                  borderBottom: `1px solid ${alpha(NAVY, 0.2)}`,
                  pb: '1px',
                  '&:hover': { borderBottom: `1px solid ${NAVY}` },
                }}
              >
                Ver cliente #{ticket.clientId}
              </Typography>
            )}
          </Box>
        )}

        {/* Message thread */}
        <Box
          sx={{
            bgcolor: SURFACE_CONTAINER_LOW,
            borderRadius: RADIUS_CARD,
            p: { xs: 1.5, md: 2 },
            mb: 2,
          }}
        >
          {ticket.AIHistory?.length === 0 && (
            <Box sx={{ textAlign: 'center', my: 6 }}>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: '0.85rem',
                  color: `${NAVY}99`,
                  mb: 1,
                }}
              >
                Esperando a que el equipo de soporte responda...
              </Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: '0.85rem', color: `${NAVY}99` }}>
                Puedes agregar un mensaje o más información si quieres.
              </Typography>
            </Box>
          )}
          {(ticket.AIHistory || [])
            .map((message, originalIndex) => ({ message, originalIndex, event: parseTicketEventName(message.name) }))
            .filter(({ message, event }) => {
              if (event) return !event.adminOnly || Boolean(user?.isAdmin);
              if (!useRegularUserView) return true;
              return Boolean(message.name);
            })
            .map(({ message, originalIndex, event }) => (
              <Box
                key={`history-${ticket.id}-${originalIndex + 1}-${message.role}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  mb: event ? 0.25 : 1.5,
                  flexDirection: 'column',
                }}
              >
                {event && <TicketMessageComponent message={message} messageIndex={originalIndex} />}
                {!event && message.role === 'user' && (
                  <TicketMessageComponent
                    message={message}
                    messageIndex={originalIndex}
                    canEdit={(user?.isAdmin && message.name?.includes('(Admin)')) || false}
                    onEdit={(messageIndex, content) => {
                      handleEditMessage(messageIndex, content).catch(console.error);
                    }}
                  />
                )}
                {!event && message.role === 'assistant' && message.content && (
                  <TicketMessageComponent message={message} messageIndex={originalIndex} />
                )}

                {message.toolCalls?.map((toolCall) => (
                  <Box
                    key={toolCall.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-start',
                      mt: 1,
                      flexDirection: 'column',
                    }}
                  >
                    {toolCall.function.name === 'listDir' && (
                      <ListDirToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'readFile' && (
                      <ReadFileToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'editFile' && (
                      <EditFileToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'editBusinessUnitPrompt' && (
                      <EditPromptToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'grepSearch' && (
                      <GrepSearchToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'executeQuery' && (
                      <ExecuteQueryToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'getClientInfo' && (
                      <GetClientInfoToolComponent args={toolCall.function.arguments} />
                    )}
                    {toolCall.function.name === 'markAnalysisComplete' && (
                      <MarkAnalysisCompleteToolComponent args={toolCall.function.arguments} />
                    )}
                    {[
                      'fileSearch',
                      'getDatabaseSchema',
                      'getBusinessUnitDetails',
                      'requestRecommendation',
                      'readLogs',
                    ].includes(toolCall.function.name) && (
                      <SimpleToolComponent toolName={toolCall.function.name} args={toolCall.function.arguments} />
                    )}
                  </Box>
                ))}

                {message.role === 'tool' && (
                  <ToolResponseComponent message={message} history={ticket.AIHistory || []} />
                )}
              </Box>
            ))}
        </Box>

        {uploadError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              fontFamily: FONT,
              borderRadius: RADIUS_CARD,
            }}
            onClose={() => setUploadError(null)}
          >
            {uploadError}
          </Alert>
        )}

        {richardSessionUrl && (
          <Alert
            severity="success"
            sx={{ mb: 2, fontFamily: FONT, borderRadius: RADIUS_CARD }}
            onClose={() => setRichardSessionUrl(null)}
          >
            Ticket enviado a Richard.{' '}
            <Link
              href={richardSessionUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ color: NAVY, fontWeight: 600 }}
            >
              Abrir la sesión de análisis
            </Link>
          </Alert>
        )}

        {richardError && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: FONT, borderRadius: RADIUS_CARD }}
            onClose={() => setRichardError(null)}
          >
            {richardError}
          </Alert>
        )}

        {/* Message composer */}
        <Box
          ref={messageInputRef}
          sx={{
            bgcolor: SURFACE_CONTAINER_LOWEST,
            borderRadius: RADIUS_CARD,
            boxShadow: SHADOW,
            p: 1.5,
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 240 }}>
            <TextField
              multiline
              minRows={1}
              maxRows={4}
              fullWidth
              placeholder="Agrega un mensaje aquí"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              onPaste={handlePasteWrapper}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  backgroundColor: SURFACE_TINT,
                  fontFamily: FONT,
                  color: NAVY,
                  '& fieldset': { borderColor: alpha(NAVY, 0.08) },
                  '&:hover fieldset': { borderColor: alpha(NAVY, 0.15) },
                  '&.Mui-focused fieldset': { borderColor: alpha(YELLOW, 0.6) },
                },
              }}
            />
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            accept="image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xls,.xlsx,video/*"
            onChange={(e) => {
              handleFileUpload(e).catch((error) => {
                console.error('Error in onChange:', error);
              });
            }}
            disabled={isUploadingFile}
          />
          {user?.isAdmin && (
            <FormControlLabel
              control={
                <Switch
                  checked={showToUser}
                  onChange={(e) => setShowToUser(e.target.checked)}
                  size="small"
                  sx={brandSwitchSx}
                />
              }
              label="Mostrar al usuario final"
              sx={{ m: 0, ...switchLabelSx }}
            />
          )}
          <IconButton
            tabIndex={-1}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingFile}
            sx={{
              height: 48,
              width: 48,
              color: `${NAVY}99`,
              borderRadius: RADIUS_ITEM,
              '&:hover': { bgcolor: SURFACE_HOVER, color: NAVY },
            }}
          >
            <AttachFileIcon />
          </IconButton>
          <Button
            onClick={() => {
              handleAddMessage().catch((error) => {
                console.error('Error in onClick:', error);
              });
            }}
            disabled={isUploadingFile || isSendingMessage || !newMessage.trim()}
            sx={{
              height: 48,
              minWidth: 48,
              px: 0,
              bgcolor: YELLOW,
              color: NAVY,
              borderRadius: RADIUS_COIN,
              boxShadow: `0 4px 12px ${alpha(YELLOW, 0.3)}`,
              '&:hover': { bgcolor: alpha(YELLOW, 0.85) },
              '&.Mui-disabled': { bgcolor: alpha(YELLOW, 0.4), color: alpha(NAVY, 0.4) },
            }}
          >
            {isUploadingFile || isSendingMessage ? <CircularProgress size={24} sx={{ color: NAVY }} /> : <SendIcon />}
          </Button>
        </Box>

        {/* Dates */}
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, flexWrap: 'wrap', mt: 1.5 }}
        >
          <Typography sx={metaTextSx}>
            Creado: {new Date(ticket.createdAt).toLocaleString('es-CL')}
            {ticket.user?.name && ` por ${ticket.user.name}`}
          </Typography>
          <Typography sx={metaTextSx}>Actualizado: {new Date(ticket.updatedAt).toLocaleString('es-CL')}</Typography>
          {ticket.deadlineDate && (
            <Typography sx={metaTextSx}>
              Fecha tope estimada: {new Date(ticket.deadlineDate).toLocaleDateString('es-CL')}
            </Typography>
          )}
        </Box>

        {ticket && (
          <EditTicketDialog
            open={editDialogOpen}
            onClose={() => setEditDialogOpen(false)}
            ticket={ticket}
            onSuccess={() => {
              refetch().catch((error) => {
                console.error('Error refetching after edit:', error);
              });
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default Ticket;
